import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import createAxiosInstance from '../utils/axios';
import * as Sentry from "@sentry/react";
import { initSocket, updateSocketAuth, disconnectSocket } from '../lib/socket';

const AuthContext = createContext();
const AuthCheckContext = createContext(() => {});

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [failedQueue, setFailedQueue] = useState([]);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const axiosInstanceRef = useRef(null);

  // Update Sentry user context whenever user changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
        ip_address: "{{auto}}"
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const logout = useCallback(async () => {
    try {
      // Only attempt server logout if axios instance is available
      if (axiosInstanceRef.current) {
        try {
          await axiosInstanceRef.current.get('/auth/logout');
        } catch (logoutError) {
          console.error('Server logout failed, continuing with local cleanup:', logoutError);
        }
      }
    } catch (e) {
      console.error('Logout preparation failed:', e);
    } finally {
      // Disconnect socket on logout
      disconnectSocket();
      
      // Always perform local cleanup regardless of server response
      localStorage.removeItem('accesstoken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeOrganization');
      localStorage.removeItem('unreadNotificationCount');
      setIsAuthenticated(false);
      setUser(null);
      setActiveOrganization(null);
      setUnreadCount(0);
      setNotifications([]);
      setIsRefreshing(false);
      setFailedQueue([]);
      Sentry.setUser(null);
    }
  }, []);

  // TODO: likely dead code — nothing ever pushes to this failedQueue (useState).
  // The actual request queuing during token refresh is handled by the separate
  // failedQueue in axios.js (a plain let variable). This version always iterates
  // over an empty array. Keeping for now until we can verify no other callers.
  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    setFailedQueue([]);
  };

  const refreshToken = useCallback(async () => {
    if (!axiosInstanceRef.current) {
      console.error('axiosInstance is not initialized');
      return null;
    }
    
    if (isRefreshing) {
      console.log('Token refresh already in progress');
      return null;
    }
    
    setIsRefreshing(true);
    
    try {
      const response = await axiosInstanceRef.current.post('/auth/refresh-token');
      const newAccessToken = response.data.accessToken;

      if (response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      if (newAccessToken) {
        localStorage.setItem('accesstoken', newAccessToken);
        updateSocketAuth(newAccessToken); // Keep socket in sync with new token
        processQueue(null, newAccessToken);
        return newAccessToken;
      } else {
        throw new Error('No access token received');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      processQueue(error, null);

      // Only clear auth state if the server explicitly rejected the refresh token (401/403).
      // Network errors (timeouts, connection refused) should NOT wipe the session —
      // the refresh token cookie may still be valid once connectivity returns.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Session is truly dead — do a full local cleanup (skip the server /auth/logout
        // call since the tokens are already invalid on the server side).
        disconnectSocket();

        localStorage.removeItem('accesstoken');
        localStorage.removeItem('user');
        localStorage.removeItem('activeOrganization');
        localStorage.removeItem('unreadNotificationCount');

        setIsAuthenticated(false);
        setUser(null);
        setActiveOrganization(null);
        setUnreadCount(0);
        setNotifications([]);
        setIsRefreshing(false);
        setFailedQueue([]);
        Sentry.setUser(null);
      }

      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const initializeAxiosInstance = () => {
      if (!axiosInstanceRef.current) {
        axiosInstanceRef.current = createAxiosInstance({
          refreshToken,
          logout
        });
      }
    };

    initializeAxiosInstance();
    checkAuth();
  }, [refreshToken, logout]);

  // Initialize socket with auth callbacks
  useEffect(() => {
    initSocket({ refreshToken, logout });
    
    return () => {
      disconnectSocket();
    };
  }, [refreshToken, logout]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('accesstoken');
      const userData = localStorage.getItem('user');
      const orgData = localStorage.getItem('activeOrganization');
      
      if (token && userData) {
        const user = JSON.parse(userData);
        setUser(user);
        setIsAuthenticated(true);
        
        if (orgData) {
          setActiveOrganization(JSON.parse(orgData));
        }
      }
    } catch (e) {
      console.error('Failed to get token or user data', e);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const verifyCode = async (tokenId, code) => {
    // Ensure axios instance exists
    if (!axiosInstanceRef.current) {
        axiosInstanceRef.current = createAxiosInstance({
            refreshToken,
            logout
        });
    }

    try {
        const response = await axiosInstanceRef.current.post('/auth/verify-magic-link', { tokenId, verificationCode: code });
        if (!response.data.error) {
            const userData = response.data.user;
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('accesstoken', response.headers['x-access-token'] || '');
            setIsAuthenticated(true);
            setNeedsOnboarding(response.data.needsOnboarding);
            return { success: true, user: userData, needsOnboarding: response.data.needsOnboarding };
        } else {
            return { success: false, error: response.data.error };
        }
    } catch (error) {
        console.error('Verification failed', error);
        const errorMessage = error.response?.data?.msg || error.message || 'An error occurred during verification';
        return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    if (activeOrganization) {
      localStorage.setItem('activeOrganization', JSON.stringify(activeOrganization));
    } else {
      localStorage.removeItem('activeOrganization');
    }
  }, [activeOrganization]);

  const fetchUnreadCount = async () => {
    if (!axiosInstanceRef.current || !isAuthenticated) {
      return;
    }
    
    try {
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const requestPromise = axiosInstanceRef.current.get('/notifications/unread-count');
      
      const response = await Promise.race([requestPromise, timeoutPromise]);
      setUnreadCount(response.data.count);
      localStorage.setItem('unreadNotificationCount', response.data.count.toString());
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Don't update the count on error to avoid UI flickering
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const cachedCount = localStorage.getItem('unreadNotificationCount');
      if (cachedCount) setUnreadCount(parseInt(cachedCount));
      
      fetchUnreadCount();
      
      const interval = setInterval(fetchUnreadCount, 30000); // Fetch unread count every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const value = {
    isAuthenticated,
    isAuthLoading,
    user,
    setUser,
    logout,
    axiosInstance: axiosInstanceRef.current,
    activeOrganization,
    setActiveOrganization,
    verifyCode,
    unreadCount,
    setUnreadCount,
    notifications,
    setNotifications,
    fetchUnreadCount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};